<template>
  <v-container>
    <v-card>
      <v-row align="center" class="py-1 px-2" no-gutters>
        <v-col cols="12" sm="6">
          <v-breadcrumbs class="px-2" :items="breadcrumbsItems"></v-breadcrumbs>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          :class="$store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                class="mx-2"
                v-bind="attrs"
                v-on="on"
                color="orange"
                @click="helpDialog = true"
              >
                <v-icon small>fa-question</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.translator("showHelp") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-container>
        <v-form ref="form" v-model="valid" @submit.prevent="getActivities">
          <v-row>
            <v-col cols="12" md="6" lg="4">
              <div>
                {{ $vuetify.lang.translator("user") }}
              </div>
              <v-row>
                <v-col>
                  <v-autocomplete
                    :items="allUsers"
                    item-text="name"
                    item-value="id"
                    :placeholder="$vuetify.lang.translator('chooseAnOption')"
                    v-model="searchFields.user_id"
                    clearable
                    autofocus
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6" lg="4">
              <div>
                {{ $vuetify.lang.translator("operations") }}
              </div>
              <v-row>
                <v-col>
                  <v-select
                    :items="allOperations"
                    item-text="name"
                    item-value="id"
                    :placeholder="$vuetify.lang.translator('chooseAnOption')"
                    v-model="searchFields.activity_type"
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6" lg="4">
              <div>
                IP
              </div>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="searchFields.ip"
                    placeholder="IP Address"
                    type="text"
                    autocomplete="on"
                    class="ltr-text"
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6" lg="6">
              <div>
                {{ $vuetify.lang.translator("description") }}
              </div>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="searchFields.activity"
                    :placeholder="
                      $vuetify.lang.translator('operationDescription')
                    "
                    type="text"
                    autocomplete="on"
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6" lg="6">
              <div>
                {{ $vuetify.lang.translator("date") }}
              </div>
              <v-row>
                <v-col cols="12" sm="6">
                  <VuePersianDatetimePicker
                    type="datetime"
                    input-class="vpdp-input"
                    format="YYYY-MM-DDTHH:mm:ssZ"
                    placeholder="YYYY/MM/DD HH:MM"
                    v-model="searchFields.created_at_from"
                    :label="$vuetify.lang.translator('dateFrom')"
                    :editable="true"
                    :locale="$store.getters.LANGUAGE == 'fa' ? 'fa' : 'en'"
                    :display-format="
                      $store.getters.LANGUAGE == 'fa'
                        ? 'jYYYY/jMM/jDD HH:mm'
                        : 'YYYY/MM/DD HH:mm'
                    "
                  ></VuePersianDatetimePicker>
                </v-col>
                <v-col cols="12" sm="6">
                  <VuePersianDatetimePicker
                    type="datetime"
                    input-class="vpdp-input"
                    format="YYYY-MM-DDTHH:mm:ssZ"
                    placeholder="YYYY/MM/DD HH:MM"
                    v-model="searchFields.created_at_to"
                    :label="$vuetify.lang.translator('dateTo')"
                    :editable="true"
                    :locale="$store.getters.LANGUAGE == 'fa' ? 'fa' : 'en'"
                    :display-format="
                      $store.getters.LANGUAGE == 'fa'
                        ? 'jYYYY/jMM/jDD HH:mm'
                        : 'YYYY/MM/DD HH:mm'
                    "
                  ></VuePersianDatetimePicker>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="auto">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                type="submit"
              >
                <v-icon class="mx-2">fa-search</v-icon
                >{{ $vuetify.lang.translator("search") }}</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>

    <v-data-table
      fixed-header
      :header-props="{ sortIcon: 'fas fa-sort-amount-up-alt' }"
      :headers="headers"
      :items="items"
      :loading="loading"
      sort-by="created_at"
      :sort-desc="true"
      :items-per-page="itemsPerPage"
      @update:items-per-page="updateRecordsPerPage"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        showFirstLastPage: true
      }"
      :options.sync="options"
      :server-items-length="totalItems"
      :dense="showDense"
      class="elevation-2 mt-3"
    >
      <template v-slot:top>
        <v-row align="center" class="my-2">
          <v-col cols="12" sm="6">
            <div class="mx-2">
              {{ $vuetify.lang.translator("searchResult") }}
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            :class="
              $store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'
            "
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  color="primary"
                  :disabled="waitForExcel"
                  @click="downloadExcel"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span v-if="waitForExcel">{{ downloadPercentage }}%</span>
                  <v-icon v-else>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("downloadExcel") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click="showDense = !showDense"
                  v-bind="attrs"
                  v-on="on"
                  :color="showDense ? 'success' : ''"
                >
                  <v-icon small>fas fa-compress-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("compactView") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.index="{ item }">
        {{
          (options.page - 1) * options.itemsPerPage + items.indexOf(item) + 1
        }}
      </template>
      <template v-slot:item.created_at="{ item }">
        <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
          {{ item.created_at | moment("jYYYY/jM/jD HH:mm") }}
        </div>
        <div class="ltr-text" v-else>
          {{ item.created_at | moment("YYYY/M/D HH:mm") }}
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div
            class="mt-6"
            v-html="$vuetify.lang.translator('searchActivitiesHelp')"
          ></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { handleError } from "@/helpers/helper";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";

export default {
  name: "SearchActivities",
  components: {
    VuePersianDatetimePicker
  },
  data: () => ({
    breadcrumbsItems: [],
    valid: false,
    searchFields: {
      user_id: null,
      activity_type: null,
      activity: null,
      created_at_from: null,
      created_at_to: null,
      ip: null
    },
    allUsers: [],
    allOperations: [],
    headers: [],
    items: [],
    itemsPerPage: 0,
    options: {},
    totalItems: 0,
    loading: false,
    waitForExcel: false,
    showDense: true,
    helpDialog: false,
    downloadPercentage: 0
  }),
  watch: {
    options: {
      handler() {
        this.getActivities();
      }
    },
    showDense: function() {
      this.$store.dispatch("saveShowDense", this.showDense ? "show" : "hide");
    }
  },
  created() {
    this.itemsPerPage = parseInt(this.$store.getters.PERPAGE);
    this.showDense = this.$store.getters.SHOWDENSE === "show";

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("management")
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("searchActivities"),
      to: { name: "SearchActivities" }
    });

    this.allOperations.push({
      id: "LOGIN",
      name: this.$vuetify.lang.translator("login")
    });
    this.allOperations.push({
      id: "LOGOUT",
      name: this.$vuetify.lang.translator("logout")
    });
    this.allOperations.push({
      id: "CHGPASSWORD",
      name: this.$vuetify.lang.translator("changePassword")
    });
    this.allOperations.push({
      id: "RESPASSWORD",
      name: this.$vuetify.lang.translator("resetPassword")
    });
    this.allOperations.push({
      id: "INSERT",
      name: this.$vuetify.lang.translator("insert")
    });
    this.allOperations.push({
      id: "EDIT",
      name: this.$vuetify.lang.translator("edit")
    });
    this.allOperations.push({
      id: "DELETE",
      name: this.$vuetify.lang.translator("delete")
    });
    this.allOperations.push({
      id: "UPLOAD",
      name: this.$vuetify.lang.translator("upload")
    });
    this.allOperations.push({
      id: "DOWNLOAD",
      name: this.$vuetify.lang.translator("download")
    });

    this.headers.push({
      text: this.$vuetify.lang.translator("row"),
      value: "index",
      sortable: false,
      align: "center"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("name"),
      value: "name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("userName"),
      value: "mobile",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("operations"),
      value: "activity_type"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("description"),
      value: "activity"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("ip"),
      value: "ip"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("createDate"),
      value: "created_at"
    });

    this.checkPrivileges();
    this.getUsers();
  },
  methods: {
    checkPrivileges() {
      let userType = parseInt(this.$store.getters.USERTYPE);
      let allowed_privileges = [1];
      if (allowed_privileges.indexOf(userType) === -1) {
        this.$router.push({ name: "Error403" });
      }
    },
    getUsers() {
      var self = this;
      this.$http
        .get("/user/list/")
        .then(function(response) {
          self.allUsers = response.data;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    getActivities() {
      let data = this.getSearchCriteria();
      if (Object.entries(data).length === 0) {
        return;
      }
      data.excel = false;
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      var parameters = null;
      if (sortBy[0]) {
        parameters = {
          page: page,
          size: itemsPerPage,
          sort: (sortDesc[0] ? "-" : "") + sortBy[0]
        };
      } else {
        parameters = {
          page: page,
          size: itemsPerPage
        };
      }
      var self = this;
      this.$http
        .post("/search/activity/", data, {
          params: parameters
        })
        .then(function(response) {
          self.items = response.data.results;
          self.totalItems = response.data.count;
          if (self.totalItems === 0) {
            self.$toasted.info(self.$vuetify.lang.translator("noDataText"), {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.loading = false;
        });
    },
    downloadExcel() {
      let data = this.getSearchCriteria();
      if (Object.entries(data).length === 0) {
        return;
      }
      data.excel = true;
      this.waitForExcel = true;
      this.downloadPercentage = 0;
      this.$http
        .post("/search/activity/", data, {
          responseType: "blob",
          onDownloadProgress: function(event) {
            self.downloadPercentage = Math.round(
              (event.loaded * 100) / event.total
            );
          }
        })
        .then(response => {
          var filename = "export";
          var disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.ms-excel"
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
          // var disposition = response.headers["content-disposition"];
          // if (disposition && disposition.indexOf("attachment") !== -1) {
          //   var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          //   var matches = filenameRegex.exec(disposition);
          //   if (matches != null && matches[1]) {
          //     filename = matches[1].replace(/['"]/g, "");
          //   }
          // }
          // const url = window.URL.createObjectURL(
          //   new Blob([response.data], { type: "application/vnd.ms-excel" })
          // );
          // const link = document.createElement("a");
          // link.href = url;
          // link.setAttribute("download", filename);
          // document.body.appendChild(link);
          // link.click();
          this.waitForExcel = false;
          this.$toasted.success(
            this.$vuetify.lang.translator("downloadStarted"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
        })
        .catch(error => {
          if (
            error.response &&
            /json$/gi.test(error.response.headers["content-type"]) &&
            error.response.status === 400
          ) {
            self.$toasted.error(self.$vuetify.lang.translator("fileNotExist"), {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          } else {
            let result = handleError(error, this.$toasted, this.$router);
            for (var item in result) {
              this.$toasted.error(result[item], {
                duration: 5000,
                keepOnHover: true,
                className:
                  self.$store.getters.LANGUAGE == "fa"
                    ? "vazir-font-fd"
                    : "vazir-font",
                iconPack: "fontawesome",
                icon: "exclamation-triangle",
                position: "top-center"
              });
            }
          }
          this.waitForExcel = false;
        });
    },
    getSearchCriteria() {
      let result = {};
      if (
        Object.values(this.searchFields).every(
          o => o === null || o === undefined || o === ""
        )
      ) {
        this.$toasted.info(this.$vuetify.lang.translator("atLeastOneOption"), {
          duration: 5000,
          keepOnHover: true,
          className:
            this.$store.getters.LANGUAGE == "fa"
              ? "vazir-font-fd"
              : "vazir-font",
          iconPack: "fontawesome",
          icon: "exclamation-triangle",
          position: "top-center"
        });
        return result;
      }
      if (this.searchFields.user_id) {
        result.user_id = this.searchFields.user_id;
      }
      if (this.searchFields.activity_type) {
        result.activity_type = this.searchFields.activity_type;
      }
      if (this.searchFields.activity) {
        result.activity = this.searchFields.activity;
      }
      if (this.searchFields.ip) {
        result.ip = this.searchFields.ip;
      }
      if (
        this.searchFields.created_at_from &&
        this.searchFields.created_at_to
      ) {
        result.created_at_from = this.searchFields.created_at_from;
        result.created_at_to = this.searchFields.created_at_to;
      }
      return result;
    },
    updateRecordsPerPage(rowsPerPage) {
      this.$store.dispatch("saveRecordsPerPage", rowsPerPage);
    }
  }
};
</script>
